import { type ReactNode, createContext, useContext, useMemo } from 'react';

import type { VariantProp } from '../common/colors';

export type Presentation = {
  /**
   * Set the most appropriate variant of the component for your use.
   */
  variant?: VariantProp;
  /**
   * Boolean to show the selected state.
   */
  selected?: boolean;
};

const PresentationContext = createContext<Presentation>({});

/**
 * Resolves the variant to render with: selection emphasises the current variant, and a
 * neutral one has nothing to emphasise so it promotes to brand.
 */
export function resolveVariant({ variant, selected }: Presentation): VariantProp {
  const base = variant ?? 'neutral';
  if (!selected) return base;
  return base === 'neutral' ? 'brand' : base;
}

/**
 * Cascades `variant` and `selected` to all descendent components.
 *
 * Nesting merges rather than replaces, so a provider setting only one axis leaves the other
 * as its ancestor left it.
 *
 * @example
 *   <PresentationProvider variant="negative">
 *     <PresentationProvider selected>
 *       <Badge>Negative and selected</Badge>
 *     </PresentationProvider>
 *   </PresentationProvider>
 */
export function PresentationProvider({
  variant,
  selected,
  children,
}: Presentation & { children: ReactNode }) {
  const inherited = useContext(PresentationContext);

  const value = useMemo(
    () => ({
      variant: variant ?? inherited.variant,
      selected: selected ?? inherited.selected,
    }),
    [variant, selected, inherited],
  );

  return <PresentationContext.Provider value={value}>{children}</PresentationContext.Provider>;
}

/**
 * Returns what ancestors cascaded down, with no defaults applied. Prefer
 * {@link usePresentation} unless "set to false" versus "never set" is load bearing.
 */
export function useInheritedPresentation(): Presentation {
  return useContext(PresentationContext);
}

/**
 * Returns the presentation a component should render with, in precedence order own prop →
 * context → default. Hand `resolvedVariant` to a styled component's `variant` prop.
 */
export function usePresentation(overrides?: Presentation) {
  const inherited = useContext(PresentationContext);

  const variant = overrides?.variant ?? inherited.variant ?? 'neutral';
  const selected = overrides?.selected ?? inherited.selected ?? false;

  return { variant, selected, resolvedVariant: resolveVariant({ variant, selected }) };
}
